import type {
  GameContext,
  GameInstance,
  GameModule,
  PreloadContext,
} from '@arcraz/arcadia-types';
import { TrenchRunInstance } from './instance.js';
import { PALETTE_HINT } from './palette.js';

/**
 * TRENCH RUN — sixth cartridge, FIRST scrolling cartridge.
 *
 * Defender (1980) + Choplifter (1982) heritage. 480x270 viewport over
 * a 1920px-wide world, camera-follows-player. Sunset palette, olive
 * desert floor, cyan hovercraft, three threat colors (red Swooper /
 * orange Mine / magenta Hunter).
 *
 * Original mechanic: **payload-weight handling** — each civilian
 * carried (max 3) shaves the hovercraft's turn rate and top speed.
 * Light = nimble. Heavy = rich payload but vulnerable. The trip back
 * to base under three civs is the cartridge's white-knuckle beat.
 *
 * Verbs: rescue (primary) + shoot (auxiliary, horizontal only — can't
 * shoot down at mines).
 */
const module_: GameModule = {
  id: 'trench-run',
  title: 'TRENCH RUN',
  manufacturer: 'ARCADIA AMUSEMENTS',
  year: 1984,
  genre: 'shooter',
  resolution: { w: 480, h: 270 },
  pixelated: true,
  paletteHint: PALETTE_HINT,
  validatorVersion: 1,
  controls: [
    { keys: 'LEFT / RIGHT', action: 'THRUST' },
    { keys: 'UP / DOWN', action: 'ASCEND / DESCEND' },
    { keys: 'Z / SPACE', action: 'FIRE' },
    { keys: 'HOVER OVER CIVIL', action: 'RESCUE (0.5s DWELL)' },
    { keys: 'LAND AT BASE', action: 'DELIVER' },
    { keys: 'Z', action: 'RESTART (AT GAME OVER)' },
  ],
  touch: {
    fireA: 'FIRE',
  },

  async preload(ctx: PreloadContext): Promise<void> {
    // Shoot — quick descending blip. Frequent input so kept short + quiet.
    ctx.registerSfx('shoot', {
      wave: 'triangle',
      pitch: [{ t: 0, f: 1200 }, { t: 30, f: 720 }],
      gain: [{ t: 0, g: 0 }, { t: 3, g: 0.1 }, { t: 30, g: 0.0 }],
    });
    // Pickup — bright rising chime when a civilian boards.
    ctx.registerSfx('pickup', {
      wave: 'triangle',
      pitch: [{ t: 0, f: 660 }, { t: 80, f: 990 }],
      gain: [{ t: 0, g: 0 }, { t: 6, g: 0.22 }, { t: 80, g: 0.0 }],
    });
    // Deliver — full triumphant fanfare (longer than pickup).
    ctx.registerSfx('deliver', {
      wave: 'square',
      pitch: [
        { t: 0, f: 440 },
        { t: 80, f: 660 },
        { t: 160, f: 880 },
        { t: 240, f: 1100 },
      ],
      gain: [{ t: 0, g: 0 }, { t: 8, g: 0.3 }, { t: 240, g: 0.0 }],
    });
    // Enemy spawn — short warble warning from the edge.
    ctx.registerSfx('enemy-spawn', {
      wave: 'sawtooth',
      pitch: [{ t: 0, f: 320 }, { t: 100, f: 220 }, { t: 200, f: 320 }],
      gain: [{ t: 0, g: 0 }, { t: 6, g: 0.16 }, { t: 200, g: 0.0 }],
      fm: { ratio: 0.5, index: 20 },
    });
    // Enemy kill — quick popping confirmation.
    ctx.registerSfx('enemy-kill', {
      wave: 'noise',
      pitch: [{ t: 0, f: 280 }, { t: 90, f: 100 }],
      gain: [{ t: 0, g: 0 }, { t: 4, g: 0.22 }, { t: 90, g: 0.0 }],
    });
    // Mine shot — low arc-launch thunk so the player can hear the
    // floor turret fire even off-screen.
    ctx.registerSfx('mine-shot', {
      wave: 'sine',
      pitch: [{ t: 0, f: 180 }, { t: 60, f: 280 }],
      gain: [{ t: 0, g: 0 }, { t: 4, g: 0.16 }, { t: 60, g: 0.0 }],
    });
    // Crash — heavy descending noise burst, payload-lost moment.
    ctx.registerSfx('crash', {
      wave: 'noise',
      pitch: [{ t: 0, f: 220 }, { t: 280, f: 60 }],
      gain: [{ t: 0, g: 0 }, { t: 8, g: 0.55 }, { t: 280, g: 0.0 }],
    });
    // Level cleared — ascending fanfare (cabinet-wide convention).
    ctx.registerSfx('level-up', {
      wave: 'square',
      pitch: [
        { t: 0, f: 440 },
        { t: 100, f: 660 },
        { t: 200, f: 880 },
      ],
      gain: [{ t: 0, g: 0 }, { t: 8, g: 0.3 }, { t: 200, g: 0.0 }],
    });
  },

  init(ctx: GameContext): GameInstance {
    return new TrenchRunInstance(ctx);
  },
};

export default module_;
